import type { PlanUpsell } from "@/lib/api/upsellHooks";

/** Keep confirmed members on retry. A lost response is retried against the
 * server's exact-offer idempotency guard, never treated as a success.
 * `onConfirmed` runs after each member so progress survives an unload. */
export async function acceptQuarterlyPlans(
  plan: PlanUpsell,
  completed: Set<string>,
  change: (item: PlanUpsell) => Promise<unknown>,
  onConfirmed?: () => void,
) {
  const failed: PlanUpsell[] = [];
  for (const item of plan.items ?? [plan]) {
    if (completed.has(item.subscription_id)) continue;
    try {
      if (plan.items && !item.paid_through) throw new Error("Missing renewal");
      await change(item);
      completed.add(item.subscription_id);
      onConfirmed?.();
    } catch {
      failed.push(item);
    }
  }
  return failed;
}

export function partialPlanMessage(plan: PlanUpsell, completed: Set<string>) {
  const members = plan.items ?? [plan];
  const names = (items: PlanUpsell[]) => items.map((item) => item.product_title).join(', ');
  const confirmed = members.filter((item) => completed.has(item.subscription_id));
  const pending = members.filter((item) => !completed.has(item.subscription_id));
  return `${confirmed.length ? `Quarterly confirmed for ${names(confirmed)}. ` : ''}Could not confirm changes for ${names(pending)}. Retry to check and finish, or review your plans in Account → Subscriptions. Your current paid month is unchanged.`;
}

/** An unfinished switch outlives the drawer. Once one member is quarterly the
 * server stops quoting the stack, so the original quote, request time and
 * confirmed members are kept per purchase until every member confirms. The
 * server revalidates the exact terms on each retry. */
export interface PlanProgress {
  sourcePurchasedAt: string;
  plan: PlanUpsell;
  requestedAt: number;
  completed: string[];
}
const progressKey = (owner: string, source: string) => `system.upsell.plan.${owner}.${source}.v1`;
export function readPlanProgress(owner: string | undefined, source: string, purchasedAt: string): PlanProgress | null {
  if (!owner) return null;
  try {
    const saved = JSON.parse(localStorage.getItem(progressKey(owner, source)) || 'null');
    return saved?.sourcePurchasedAt === purchasedAt && saved.plan?.subscription_id &&
      saved.requestedAt > 0 && Array.isArray(saved.completed) ? saved : null;
  } catch { return null; }
}
export function savePlanProgress(owner: string | undefined, source: string, progress: PlanProgress | null) {
  if (!owner) return;
  try {
    if (progress) localStorage.setItem(progressKey(owner, source), JSON.stringify(progress));
    else localStorage.removeItem(progressKey(owner, source));
  } catch { /* storage is optional; Account → Subscriptions remains the fallback */ }
}
